import * as React from 'react'
import { OverlayWrapper } from '../core/overlay'
import {
  Sheet,
  SheetOverlay,
  SheetHeader,
  SheetTitle,
  SheetBody,
} from '../core/sheet'
import { Badge } from '../core/badge'
import { SearchBar } from '../core/search-bar'
import { Separator } from '../core/separator'
import { type EntityRef } from '../core/entity-glyph'
import { toCardEntity } from './action-center-card'
import {
  CompletedActionCard,
  type CompletedActionCardProps,
} from './completed-action-card'

/**
 * CompletedActionsPanel — the "Recently completed" history behind the Action
 * Center's **Completed** button. A right-side `Sheet` drawer (matching the
 * {@link ActionCenterPanel}) with a pinned search and the finished
 * recommendations grouped by day, each a {@link CompletedActionCard} showing what
 * was done (or why it auto-resolved) and when.
 *
 *   <CompletedActionsPanel
 *     trigger={<Button appearance="outline">Completed</Button>}
 *     count="2/13"
 *     items={completed}
 *   />
 *
 * Presentational — it groups, searches, and renders the `items` you pass; the
 * consumer owns fetching and pagination. Search matches the title, linked-record
 * names, the outcome, and any `keywords`. Items carry a `group` heading (e.g.
 * "Today, Jul 11") for the day sections and a `time` for the trailing stamp; an
 * item can be `loading` to show a skeleton body while its detail streams in.
 */

export interface CompletedActionItem
  extends Omit<CompletedActionCardProps, 'entities'> {
  /** Stable id. */
  id: string
  /** Day-group heading, e.g. "Today, Jul 11". Items sharing it are grouped. */
  group: string
  /** Linked records (mapped to the card's glyph via their type). */
  entities?: EntityRef[]
  /** Extra searchable text beyond the title, record names, and outcome. */
  keywords?: string
}

export interface CompletedActionsPanelProps {
  /** Element that opens the panel — its `onClick` is wired through. */
  trigger: React.ReactElement
  /** Completed recommendations, newest first. */
  items: CompletedActionItem[]
  /** Panel heading. Default "Recently completed". */
  title?: React.ReactNode
  /** Count pill beside the title, e.g. "2/13". */
  count?: React.ReactNode
  /** Accessible label for the count pill (e.g. "2 of 13 completed") — "2/13"
   *  alone reads as "2 slash 13" to assistive tech. */
  countLabel?: string
  /** Search input placeholder. */
  searchPlaceholder?: string
  /** Shown when there are no completed items at all. */
  emptyState?: React.ReactNode
  /** Shown when a search matches nothing. */
  noResultsState?: React.ReactNode
  /** Classes for the drawer panel — e.g. its max-width. Default `max-w-xl`. */
  panelClassName?: string
  isOpen?: boolean
  defaultOpen?: boolean
  onOpenChange?: (isOpen: boolean) => void
}

const asText = (node: React.ReactNode) =>
  typeof node === 'string' ? node : typeof node === 'number' ? String(node) : ''

/** Everything about an item a text search should match. */
const searchTextFor = (item: CompletedActionItem) =>
  [
    asText(item.title),
    asText(item.status.label),
    ...(item.entities?.map((e) => asText(e.name)) ?? []),
    item.keywords ?? '',
  ]
    .join(' ')
    .toLowerCase()

interface DayGroup {
  label: string
  items: CompletedActionItem[]
}

/** Group items by their `group` heading, preserving first-seen order. */
function groupByDay(items: CompletedActionItem[]): DayGroup[] {
  const buckets = new Map<string, CompletedActionItem[]>()
  const order: string[] = []
  for (const item of items) {
    if (!buckets.has(item.group)) {
      buckets.set(item.group, [])
      order.push(item.group)
    }
    buckets.get(item.group)!.push(item)
  }
  return order.map((label) => ({ label, items: buckets.get(label)! }))
}

interface CompletedActionsContentProps {
  items: CompletedActionItem[]
  searchPlaceholder: string
  emptyState: React.ReactNode
  noResultsState: React.ReactNode
  title: React.ReactNode
  count?: React.ReactNode
  countLabel?: string
}

function CompletedActionsContent({
  items,
  searchPlaceholder,
  emptyState,
  noResultsState,
  title,
  count,
  countLabel,
}: CompletedActionsContentProps) {
  const [query, setQuery] = React.useState('')
  const trimmed = query.trim().toLowerCase()

  const filtered = React.useMemo(
    () =>
      trimmed
        ? items.filter((item) => searchTextFor(item).includes(trimmed))
        : items,
    [items, trimmed]
  )
  const groups = React.useMemo(() => groupByDay(filtered), [filtered])

  return (
    <>
      <SheetHeader>
        <div className="flex items-center gap-2">
          <SheetTitle level={2} className="text-lg">
            {title}
          </SheetTitle>
          {count != null && (
            <Badge color="gray" size="sm" aria-label={countLabel}>
              {count}
            </Badge>
          )}
        </div>
      </SheetHeader>

      <div className="px-6 pb-4 pt-4">
        <SearchBar
          size="md"
          // md text + height, but slim the focus ring back to 2px (md's default
          // is a 4px ring, which reads as a heavy glow on this wide field).
          containerClassName="focus-within:!ring-2"
          placeholder={searchPlaceholder}
          value={query}
          onValueChange={setQuery}
          clearable
          aria-label="Search completed actions"
        />
      </div>

      <SheetBody className="space-y-6 pt-0">
        {groups.length === 0 ? (
          <p role="status" className="py-10 text-center text-sm text-fbui-gray-500">
            {trimmed ? noResultsState : emptyState}
          </p>
        ) : (
          groups.map((group) => (
            <section key={group.label}>
              <div className="mb-3 flex items-center gap-3">
                {/* h3 (under the panel's h2 title) so the day groups are
                    navigable by heading. */}
                <h3 className="shrink-0 text-xs font-semibold uppercase tracking-wide text-fbui-gray-500">
                  {group.label}
                </h3>
                <Separator decorative className="flex-1" />
              </div>
              <div className="space-y-3">
                {group.items.map((item) => (
                  <CompletedActionCard
                    key={item.id}
                    title={item.title}
                    status={item.status}
                    ai={item.ai}
                    steps={item.steps}
                    description={item.description}
                    loading={item.loading}
                    entities={item.entities?.map(toCardEntity)}
                    time={item.time}
                    size={item.size ?? 'sm'}
                  />
                ))}
              </div>
            </section>
          ))
        )}
      </SheetBody>
    </>
  )
}

export const CompletedActionsPanel = ({
  trigger,
  items,
  title = 'Recently completed',
  count,
  countLabel,
  searchPlaceholder = 'Search by company, deal, contact, or keyword',
  emptyState = 'Nothing completed yet.',
  noResultsState = 'No completed actions match your search.',
  panelClassName = 'max-w-xl',
  isOpen,
  defaultOpen,
  onOpenChange,
}: CompletedActionsPanelProps) => (
  <OverlayWrapper
    trigger={trigger}
    isOpen={isOpen}
    defaultOpen={defaultOpen}
    onOpenChange={onOpenChange}
  >
    {/* A right-side drawer, matching the Action Center Panel. Remounts each open
        (the overlay unmounts children on close) so the search box resets. */}
    <SheetOverlay side="right" className={panelClassName}>
      <Sheet>
        <CompletedActionsContent
          items={items}
          searchPlaceholder={searchPlaceholder}
          emptyState={emptyState}
          noResultsState={noResultsState}
          title={title}
          count={count}
          countLabel={countLabel}
        />
      </Sheet>
    </SheetOverlay>
  </OverlayWrapper>
)
CompletedActionsPanel.displayName = 'CompletedActionsPanel'
